/**
 * `/assets/:divisionId` — a division page. The port of the demo's
 * `renderDivision(v, sub)` (demo line 2152).
 *
 * One of three: Surface Drilling, Underground Drilling, Underground Load &
 * Haul. `?subclass=` narrows the page to one class and is what the sidebar's
 * six indented rows link to.
 *
 * The page reads top to bottom as: what the class costs and how available it
 * is → what it looks like, with the flagged components called out → every
 * machine, grouped by the mine it works at.
 *
 * ← and → step the first hero through its subclass (demo line 2171); they are
 * ignored while typing or with a modifier held (`useArrowStep`).
 *
 * ## Data
 *
 * `telemetry` — `daily_metric` and `shift_daily` (both columnar, unpacked
 * once) plus `energy`. The machine register, the condition register and the
 * cost-per-machine table are eager.
 *
 * ## Declared omissions
 *
 * - **The 360° turntable still.** The demo preferred `turnStill(f, …)` over the
 *   vector drawing when the turntable frames were loaded; the turntable is
 *   Tier 3 (11.7 MB of WebP), so the hero always draws the vector illustration
 *   with the studio render beside it, as the machine page does.
 * - **One drawer per KPI card, not three.** The demo wrapped the availability
 *   and cost-per-hour figures in separate `FIG(...)` links. The port makes the
 *   whole card one button that opens the availability rows — the week's shift
 *   records for the card's machines — with the calculation box above them.
 *
 * @see src/versions/v1/modules/fleet/DivisionScene.transform.ts
 * @see docs/logic/fleet/division.md
 */

import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Drill } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  InlineError,
  SkeletonCardGrid,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { MINES, WORKINGS } from '../../data/core';
import { CONDITION_BAND_LABEL } from '../../schema/machine';
import { unpackTable } from '../../shared/columnar';
import { CLSNAME, healthOf, MINE_COL, statusOf } from '../../shared/oem-engine';
import { MeasureTile } from '../../shared/oem-tiles';
import { useSourceDrawer } from '../issues/useSourceDrawer';
import { useOemData, type DailyMetricRow, type ShiftDailyRow } from '../../shared/useOemData';
import { hasIllustration, MachineIllustration } from '../../shared/viz/MachineIllustration';
import { SideRender } from '../../shared/viz/SideRender';
import { useOemDocIdentity } from '../../shared/docs/useOemDocIdentity';
import './palette';
import { divisionFleet } from './AssetsSectionScene.transform';
import { MachineCard } from './MachineCard';
import { useArrowStep } from './useArrowStep';
import {
  divisionHeroes,
  divisionKpis,
  divisionMineSections,
  divisionStandfirst,
  divisionTabs,
  divisionTitle,
  heroOptionLabel,
  parseDivision,
  parseSubclass,
  stepPeer,
  type DivisionCtx,
  type DivisionHero,
} from './DivisionScene.transform';

export function DivisionScene() {
  const { divisionId } = useParams<{ divisionId: string }>();
  const [search] = useSearchParams();
  const division = parseDivision(divisionId);
  const subclass = division ? parseSubclass(division, search.get('subclass')) : null;

  /** Demo line 2157 — `DIVSEL`: the last machine shown per subclass. */
  const [selected, setSelected] = useState<Record<string, string | undefined>>({});

  const { data, loading, error } = useOemData('telemetry');

  const ctx = useMemo<DivisionCtx | null>(() => {
    if (!data) return null;
    return {
      dailyMetric: unpackTable<DailyMetricRow>(data.daily_metric),
      shiftDaily: unpackTable<ShiftDailyRow>(data.shift_daily),
      energy: data.energy,
    };
  }, [data]);

  const machines = useMemo(
    () => (division ? divisionFleet(division, subclass) : []),
    [division, subclass],
  );
  const { openDataset } = useSourceDrawer();
  const kpis = useMemo(
    () => (division && ctx ? divisionKpis(machines, division, subclass, ctx) : []),
    [ctx, division, machines, subclass],
  );
  const heroes = useMemo(
    () => (division ? divisionHeroes(machines, division, subclass, selected) : []),
    [division, machines, selected, subclass],
  );
  const sections = useMemo(
    () => (ctx ? divisionMineSections(machines, ctx) : []),
    [ctx, machines],
  );

  // Demo line 2171: ←/→ step the first hero — the narrowed subclass, or the
  // division's first.
  const firstHero = heroes[0];
  useArrowStep(
    firstHero
      ? (delta) =>
          setSelected((prev) => ({
            ...prev,
            [firstHero.subclass]: stepPeer(firstHero.peers, firstHero.machine.machine_id, delta),
          }))
      : null,
  );

  if (!division) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody as="div">
          <EmptyState
            icon={<Drill className="mx-auto h-8 w-8 text-zinc-400" strokeWidth={1.6} />}
            title="Unknown division"
            description="The three divisions are Surface Drilling, Underground Drilling and Underground Load & Haul."
            action={
              <Link
                to="/assets"
                className="text-sm text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
              >
                Back to Assets
              </Link>
            }
          />
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const tabs = divisionTabs(division, subclass);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="DivisionScene">
        <SurfacePageBody className="@container max-w-[1320px] space-y-8" as="div">
          <SurfacePageHeader
            kicker="Assets"
            title={divisionTitle(division, subclass)}
            description={divisionStandfirst(division, subclass, machines)}
            actions={<HelpAnchor doc="fleet/division" />}
          />

          <Commentable name="DivisionTabs">
            <nav aria-label="Filter by class" className="flex flex-wrap gap-1.5">
              {tabs.map((tab) => (
                <Link
                  key={tab.id}
                  to={tab.to}
                  aria-current={tab.active ? 'page' : undefined}
                  className={cn(
                    'rounded-full border px-3 py-1.5 text-[12.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]',
                    tab.active
                      ? 'font-medium'
                      : 'hover:bg-[var(--surface-1)]',
                  )}
                  style={
                    tab.active
                      ? {
                          borderColor: 'var(--accent)',
                          // `--accent-text` is white — the ink that sits ON
                          // the accent. On an accent-soft tint the accent
                          // itself is what reads.
                          color: 'var(--accent)',
                          background: 'var(--accent-soft)',
                        }
                      : { borderColor: 'var(--hairline)', color: 'var(--ink-secondary)' }
                  }
                >
                  {tab.label}
                </Link>
              ))}
            </nav>
          </Commentable>

          <AskStrip view="fleet" />

          {error ? (
            <InlineError>{`Telemetry did not load: ${error.message}`}</InlineError>
          ) : null}

          <Commentable name="DivisionKpis">
            {loading || !ctx ? (
              <SkeletonCardGrid count={3} />
            ) : (
              <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @4xl:grid-cols-3">
                {kpis.map((kpi) => (
                  <MeasureTile
                    key={kpi.label}
                    eyebrow={`${kpi.label} · ${kpi.machines} machines`}
                    value={kpi.stat.avail ?? '—'}
                    unit="% availability"
                    onSelect={() =>
                      openDataset('shift', {
                        title: `Shift records · ${kpi.label} · week to 06 Sep`,
                        ...kpi.source,
                      })
                    }
                    selectHint="Open the shift records behind this availability figure"
                    detail={
                      <>
                        {`A$${kpi.stat.cph ?? '—'}`} per operating hour ·{' '}
                        {`${kpi.stat.co2 ?? '—'}`} kg CO₂-e/h
                      </>
                    }
                  />
                ))}
              </div>
            )}
          </Commentable>

          <Commentable name="DivisionHeroes">
            <div
              className={cn(
                'grid grid-cols-1 gap-4',
                heroes.length > 1 ? '@4xl:grid-cols-2' : '',
              )}
            >
              {heroes.map((hero) => (
                <DivisionHeroCard
                  key={hero.subclass}
                  hero={hero}
                  onPick={(machineId) =>
                    setSelected((prev) => ({ ...prev, [hero.subclass]: machineId }))
                  }
                />
              ))}
            </div>
          </Commentable>

          {loading || !ctx ? (
            <SkeletonCardGrid count={8} />
          ) : sections.length === 0 ? (
            <EmptyState
              icon={<Drill className="mx-auto h-8 w-8 text-zinc-400" strokeWidth={1.6} />}
              title="No machines"
              description="No machine of this class is in the register."
            />
          ) : (
            sections.map((section) => (
              <section key={section.mine} className="space-y-3">
                <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-divider pb-2">
                  <span
                    aria-hidden="true"
                    className="inline-block h-3 w-3 rounded-sm"
                    style={{ background: MINE_COL[section.mine] }}
                  />
                  <h2 className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">
                    {section.name}
                  </h2>
                  <span className="text-xs text-muted">{section.detail}</span>
                  <Link
                    to={`/sites/${section.mine}`}
                    className="ml-auto text-xs text-accent-700 underline underline-offset-2 hover:no-underline dark:text-accent-400"
                  >
                    Site page
                  </Link>
                </header>
                <div className="grid grid-cols-1 gap-3 @md:grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4">
                  {section.cards.map((card) => (
                    <MachineCard key={card.machine.machine_id} card={card} />
                  ))}
                </div>
              </section>
            ))
          )}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

/**
 * Demo line 2157. `divHero` — the representative machine for one subclass,
 * with the stepper, the drawing and the studio render.
 */
function DivisionHeroCard({
  hero,
  onPick,
}: {
  hero: DivisionHero;
  onPick: (machineId: string) => void;
}) {
  const f = hero.machine;
  const band = statusOf(f.machine_id);
  const health = healthOf(f.machine_id);
  const drawable = hasIllustration(f.cls);
  const oem = useOemDocIdentity();

  return (
    <section
      className="flex flex-col gap-3 rounded-xl border p-4"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.1em]"
          style={{ color: 'var(--ink-tertiary)' }}
        >
          {`${hero.subclass} · ${CLSNAME[f.cls]}`}
        </span>
        <span className="flex-1" />
        <button
          type="button"
          title="Previous machine in this class"
          aria-label="Previous machine in this class"
          onClick={() => onPick(stepPeer(hero.peers, f.machine_id, -1))}
          className="inline-flex h-7 w-7 items-center justify-center rounded-lg border transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
          style={{ borderColor: 'var(--hairline)', color: 'var(--ink-secondary)' }}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <label className="sr-only" htmlFor={`divhero-${hero.subclass.replace(/\W/g, '')}`}>
          {`Machine shown for ${hero.subclass}`}
        </label>
        <select
          id={`divhero-${hero.subclass.replace(/\W/g, '')}`}
          value={f.machine_id}
          onChange={(e) => onPick(e.target.value)}
          className="max-w-[min(100%,22rem)] truncate rounded-lg border px-2 py-1 text-[12px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
          style={{
            borderColor: 'var(--hairline)',
            background: 'var(--surface-1)',
            color: 'var(--ink-primary)',
          }}
        >
          {hero.peers.map((peer) => (
            <option key={peer.machine_id} value={peer.machine_id}>
              {heroOptionLabel(peer, Boolean(WORKINGS[peer.mine]))}
            </option>
          ))}
        </select>
        <button
          type="button"
          title="Next machine in this class"
          aria-label="Next machine in this class"
          onClick={() => onPick(stepPeer(hero.peers, f.machine_id, 1))}
          className="inline-flex h-7 w-7 items-center justify-center rounded-lg border transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
          style={{ borderColor: 'var(--hairline)', color: 'var(--ink-secondary)' }}
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
        <span className="mono text-[11px] text-muted">{`${hero.position} / ${hero.peers.length}`}</span>
      </div>

      {drawable ? (
        <MachineIllustration
          machineId={f.machine_id}
          model={f.model}
          cls={f.cls}
          view="side"
          health={health}
        />
      ) : (
        <SideRender
          model={f.model}
          alt={`${f.model} side elevation, studio render`}
          className="w-full rounded-lg"
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted">
        <span className="min-w-0">
          <span className={`band-pill band-${band}`}>{CONDITION_BAND_LABEL[band]}</span>{' '}
          {`${f.machine_id} · ${MINES[f.mine].name} · ${f.engine_hours.toLocaleString('en-AU')} h · side elevation, studio render of the ${oem.short} ${f.model} · flagged components carry a callout`}
        </span>
        <Link
          to={`/machines/${encodeURIComponent(f.machine_id)}`}
          className="shrink-0 rounded-lg border px-2.5 py-1 text-[11.5px] transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
          style={{ borderColor: 'var(--hairline)', color: 'var(--ink-primary)' }}
        >
          {`Open ${f.machine_id}`}
        </Link>
      </div>
    </section>
  );
}
